import type { ComponentType } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Briefcase, Star } from 'lucide-react';
import {
  DashIcon,
  FolderIcon,
  HomeIcon,
  InvestorsIcon,
  OriginIcon,
  PipelineIcon,
  SettingsIcon,
  TaskIcon,
} from '@/components/icons/NavIcons';
import { useMe } from '@/features/identity/useMe';
import { useDealDrawer } from '@/features/pipeline/dealDrawerStore';
import { useDeals } from '@/features/pipeline/useDeals';
import { useInvestmentDrawer } from '@/features/portfolio/investmentDrawerStore';
import { useInvestments } from '@/features/portfolio/useInvestments';
import { useTasks } from '@/features/tasks/api';
import type { TaskListItem } from '@/features/tasks/types';
import { stageByApiName } from '@/lib/domain/stages';
import { cn } from '@/lib/utils';
import { useShellStore } from './shellStore';

type NavIcon = ComponentType<{ size?: number; strokeWidth?: number }>;
type ModuleId = 'pipeline' | 'portfolio' | 'ri';
type CountKey = 'funil' | 'tarefasPipeline' | 'investimentos' | 'tarefasPortfolio';

/**
 * Sidebar de 2 níveis do Portal Vega (Bloco 1 — DEC-060, fiel a design/mocks/portal-vega-home-mock.html):
 * na home mostra "Início" + os 3 módulos; dentro de um módulo mostra "Início" + o nome do módulo +
 * as abas dele + o bloco contextual (Deals prioritários / Investimentos ativos). Configurações
 * segue transversal no rodapé, em ambos os níveis (gate inalterado).
 */

// Abas de cada módulo (nível 2). RI ganha sua 1ª aba real na Fatia A (ADR-0013) — só Potenciais
// Investidores; Dashboards/Fundos em Captação/Ativos/Encerrados do mock são fatias futuras.
const MODULE_TABS: Record<
  ModuleId,
  ReadonlyArray<{ to: string; label: string; icon: NavIcon; countKey?: CountKey }>
> = {
  pipeline: [
    { to: '/pipeline', label: 'Funil', icon: PipelineIcon, countKey: 'funil' },
    { to: '/pipeline/tarefas', label: 'Tarefas', icon: TaskIcon, countKey: 'tarefasPipeline' },
    { to: '/pipeline/originacao', label: 'Originação', icon: OriginIcon },
    { to: '/pipeline/dashboards', label: 'Dashboards', icon: DashIcon },
  ],
  portfolio: [
    { to: '/portfolio', label: 'Investimentos', icon: FolderIcon, countKey: 'investimentos' },
    { to: '/portfolio/tarefas', label: 'Tarefas', icon: TaskIcon, countKey: 'tarefasPortfolio' },
  ],
  ri: [{ to: '/ri/investidores', label: 'Potenciais Investidores', icon: InvestorsIcon }],
};

const MODULE_NAMES: Record<ModuleId, string> = {
  pipeline: 'Pipeline',
  portfolio: 'Portfólio',
  ri: 'Investidores',
};

// 3 cartões de módulo da home (nível 1). Rótulo "Investidores" (não cabe o nome completo em
// 224px com o chip) — nome completo aparece no cartão da home/título da página (DOCUMENTACAO §3.1).
// "Investidores" deixou de ser "em breve" na Fatia A (DEC-079, reverte a DEC-074) — tem tela real
// agora (Potenciais Investidores).
const HOME_MODULES = [
  { to: '/pipeline', label: 'Pipeline', icon: PipelineIcon, soon: false },
  { to: '/ri', label: 'Investidores', icon: InvestorsIcon, soon: false },
  { to: '/portfolio', label: 'Portfólio', icon: FolderIcon, soon: false },
] as const;

function moduleFor(pathname: string): ModuleId | null {
  if (pathname === '/pipeline' || pathname.startsWith('/pipeline/')) return 'pipeline';
  if (pathname === '/portfolio' || pathname.startsWith('/portfolio/')) return 'portfolio';
  if (pathname === '/ri' || pathname.startsWith('/ri/')) return 'ri';
  return null;
}

function myOpenCount(items: TaskListItem[] | undefined, myUserId: string | undefined): number {
  return (items ?? []).filter(
    (t) => t.assigneeUserId === myUserId && t.state !== 'Done' && t.state !== 'Cancelled',
  ).length;
}

export function Sidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const collapsed = useShellStore((s) => s.sidebarCollapsed);
  const openDeal = useDealDrawer((s) => s.openDeal);
  const openInvestment = useInvestmentDrawer((s) => s.openInvestment);
  const me = useMe(true);
  const activeModule = moduleFor(pathname);
  const { data: deals } = useDeals();
  // Reusa a query key ['investments'] da PortfolioPage (mesmo cache); filtra Ativos client-side.
  const { data: investments } = useInvestments();
  // Contadores de Tarefas por módulo: filtro SERVER-SIDE por linkType (mesmo padrão do D1 —
  // TarefasPage/TasksSection já usam `linkType`) — eleva o teto prático de /api/tasks pra 200 POR
  // MÓDULO em vez de 200 no total (achado F07, docs/audit-tarefas-2026-07-28.md, segue existindo,
  // só mitigado). `enabled` só busca a query do módulo ativo — a Sidebar é global e monta em toda
  // página, então não faz sentido pedir os 2 linkTypes fora do módulo correspondente.
  const { data: pipelineTasks } = useTasks({ linkType: 'Deal' }, activeModule === 'pipeline');
  const { data: portfolioTasks } = useTasks(
    { linkType: 'Investment' },
    activeModule === 'portfolio',
  );

  const priority = (deals?.deals ?? []).filter((d) => d.isPriority);
  const activeInvestments = (investments ?? []).filter((i) => i.status === 'Active');

  // Badge do Funil = total de Ativos no servidor (não items.length, que capa em 200).
  const counts: Record<CountKey, number | undefined> = {
    funil: deals?.total,
    tarefasPipeline: pipelineTasks ? myOpenCount(pipelineTasks.items, me.data?.userId) : undefined,
    investimentos: investments?.length,
    tarefasPortfolio: portfolioTasks ? myOpenCount(portfolioTasks.items, me.data?.userId) : undefined,
  };

  const itemClass = (isActive: boolean) =>
    cn(
      'flex items-center rounded text-[13px] font-medium transition-colors',
      collapsed ? 'justify-center py-2' : 'gap-2.5 px-2.5 py-1.5',
      isActive
        ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold shadow-sm'
        : 'text-sidebar-foreground/80 hover:bg-white/[0.06]',
    );

  const isHome = pathname === '/';

  return (
    <nav
      className={cn(
        'flex shrink-0 flex-col bg-sidebar py-3.5 text-sidebar-foreground transition-[width] duration-150',
        collapsed ? 'w-14 px-1.5' : 'w-[224px] px-2.5',
      )}
    >
      <div
        className={cn(
          'mb-1 flex items-center border-b border-white/10 pb-3.5',
          collapsed ? 'justify-center pt-1.5' : 'px-1.5 pt-1',
        )}
      >
        {collapsed ? (
          <img src="/brand/vega-icon.jpg" alt="Vega" className="block h-[22px] w-auto" />
        ) : (
          <img src="/brand/vega-logo.jpg" alt="Vega Asset" className="block h-auto w-14" />
        )}
      </div>

      {/* "Início" fica sempre no topo, nos dois níveis (§5 — volta pra home aprovada). */}
      <ul className="mt-1 flex flex-col gap-0.5">
        <li>
          <Link to="/" title={collapsed ? 'Início' : undefined} className={itemClass(isHome)}>
            <HomeIcon size={15} strokeWidth={isHome ? 1.9 : 1.6} />
            {!collapsed && <span className="flex-1">Início</span>}
          </Link>
        </li>
      </ul>

      {activeModule === null ? (
        <>
          {!collapsed && (
            <p className="mt-4 px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-sidebar-foreground/45">
              Módulos
            </p>
          )}
          <ul className={cn('flex flex-col gap-0.5', collapsed && 'mt-1')}>
            {HOME_MODULES.map((m) => {
              const Icon = m.icon;
              const content = (
                <>
                  <Icon size={15} strokeWidth={1.6} />
                  {!collapsed && <span className="flex-1">{m.label}</span>}
                  {!collapsed && m.soon && (
                    <span className="rounded-[3px] bg-white/[0.09] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-sidebar-foreground/55">
                      em breve
                    </span>
                  )}
                </>
              );
              const itemContainerClass = cn(
                'flex items-center rounded text-[13px] font-medium transition-colors',
                collapsed ? 'justify-center py-2' : 'gap-2.5 px-2.5 py-1.5',
                m.soon
                  ? 'cursor-default text-sidebar-foreground/[0.42]'
                  : 'text-sidebar-foreground/80 hover:bg-white/[0.06]',
              );
              return (
                <li key={m.to}>
                  {/* DEC-079 (Fatia A) reverte a DEC-060/074: "Investidores" tinha esse item inerte
                      na sidebar (fiel ao portal-vega-home-mock.html, .nav-dim{'{'}cursor:default{'}'})
                      enquanto o módulo era só "em breve". Com a Fatia A entregando a 1ª tela real
                      (Potenciais Investidores), o item volta a ser clicável — o mock do RI é que
                      passa a mandar nesta parte da navegação, não mais o mock da home do Bloco 1. */}
                  {m.soon ? (
                    <div title={collapsed ? m.label : undefined} className={itemContainerClass}>
                      {content}
                    </div>
                  ) : (
                    <Link to={m.to} title={collapsed ? m.label : undefined} className={itemContainerClass}>
                      {content}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <>
          {!collapsed && (
            <p className="mt-4 px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-sidebar-foreground/45">
              {MODULE_NAMES[activeModule]}
            </p>
          )}
          <ul className={cn('flex flex-col gap-0.5', collapsed && 'mt-1')}>
            {MODULE_TABS[activeModule].map((item) => {
              const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`);
              const Icon = item.icon;
              const count = item.countKey ? counts[item.countKey] : undefined;
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    title={collapsed ? item.label : undefined}
                    className={itemClass(isActive)}
                  >
                    <Icon size={15} strokeWidth={isActive ? 1.9 : 1.6} />
                    {!collapsed && <span className="flex-1">{item.label}</span>}
                    {!collapsed && count != null && (
                      <span
                        className={cn(
                          'font-mono text-[10.5px] font-medium',
                          isActive ? 'text-vega-muted-2' : 'text-sidebar-foreground/40',
                        )}
                      >
                        {count}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

          {activeModule === 'pipeline' && !collapsed && (
            <div className="mt-4 border-t border-white/10 pt-2">
              <p className="px-2.5 text-[10px] font-semibold uppercase tracking-wide text-sidebar-foreground/45">
                Deals prioritários
              </p>
              {priority.length === 0 ? (
                <p className="px-2.5 py-2 text-xs text-sidebar-foreground/40">Nenhum marcado.</p>
              ) : (
                <ul className="flex flex-col gap-0.5 py-1">
                  {priority.map((d) => (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => openDeal(d)}
                        title={d.codename || d.companyName}
                        className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-xs font-medium text-sidebar-foreground/85 hover:bg-white/[0.06]"
                      >
                        <Star size={9} className="shrink-0 text-vega-gold" fill="currentColor" />
                        <span className="flex-1 truncate">{d.codename || d.companyName}</span>
                        <span className="shrink-0 font-mono text-[9px] uppercase tracking-wide text-sidebar-foreground/45">
                          {stageByApiName(d.stage)?.short ?? ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {activeModule === 'portfolio' && !collapsed && (
            <div className="mt-4 border-t border-white/10 pt-2">
              <p className="px-2.5 text-[10px] font-semibold uppercase tracking-wide text-sidebar-foreground/45">
                Investimentos ativos
              </p>
              {activeInvestments.length === 0 ? (
                <p className="px-2.5 py-2 text-xs text-sidebar-foreground/40">Nenhum ativo.</p>
              ) : (
                <ul className="flex flex-col gap-0.5 py-1">
                  {activeInvestments.map((i) => (
                    <li key={i.id}>
                      <button
                        type="button"
                        onClick={() => openInvestment(i)}
                        title={i.codename || i.companyName}
                        className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-xs font-medium text-sidebar-foreground/85 hover:bg-white/[0.06]"
                      >
                        <Briefcase size={9} className="shrink-0 text-sidebar-foreground/50" />
                        <span className="flex-1 truncate">{i.codename || i.companyName}</span>
                        <span className="shrink-0 truncate font-mono text-[9px] uppercase tracking-wide text-sidebar-foreground/45">
                          {i.fundName}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {/* Configurações fixada no rodapé (fiel ao mock), gated a admin (E8) — transversal aos 2 níveis. */}
      {me.data?.isAdmin && (
        <div className="mt-auto border-t border-white/10 pt-2.5">
          <Link
            to="/config"
            title={collapsed ? 'Configurações' : undefined}
            className={cn(
              'flex items-center rounded text-[13px] font-medium transition-colors',
              collapsed ? 'justify-center py-2' : 'gap-2.5 px-2.5 py-1.5',
              pathname.startsWith('/config')
                ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold shadow-sm'
                : 'text-sidebar-foreground/70 hover:bg-white/[0.06]',
            )}
          >
            <SettingsIcon size={14} strokeWidth={pathname.startsWith('/config') ? 1.9 : 1.6} />
            {!collapsed && <span>Configurações</span>}
          </Link>
        </div>
      )}
    </nav>
  );
}
